import { z } from "zod";
import {
  learningTransaction,
  PRACTICE_STORE,
  META_STORE,
  learningGeneration,
  openLearningDatabase,
} from "../review/database.ts";
import type { BranchExposure, PracticeSession, PracticeFeedback } from "./types.ts";

export const practiceRecordSchema = z.object({
  id: z.string().min(1).max(1000),
  kind: z.enum(["exposure", "checkpoint", "session"]),
  updatedAt: z.number().finite(),
  value: z.record(z.unknown()),
});
export type PracticeRecord = z.infer<typeof practiceRecordSchema>;

export type PracticeCheckpoint = {
  session: PracticeSession;
  route: string[];
  feedback: PracticeFeedback | null;
  review?: {
    queue: string[];
    returns: string[];
    answers: unknown[];
    total: number;
    cardId: string | null;
    pending: { nextId: string | null; againId: string | null } | null;
  };
};

export type PracticeStore = {
  generation?(): Promise<number>;
  list(): Promise<PracticeRecord[]>;
  get(id: string): Promise<PracticeRecord | null>;
  put(record: PracticeRecord, generation?: number): Promise<void>;
  remove(id: string, generation?: number): Promise<void>;
  replace(records: PracticeRecord[]): Promise<void>;
};

const copy = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
export function createMemoryPracticeStore(seed: PracticeRecord[] = []): PracticeStore {
  const records = new Map(seed.map((record) => [record.id, copy(record)]));
  let generation = 0;
  return {
    async generation() {
      return generation;
    },
    async list() {
      return copy([...records.values()]);
    },
    async get(id) {
      return copy(records.get(id) || null);
    },
    async put(record, expectedGeneration) {
      if (expectedGeneration !== undefined && expectedGeneration !== generation)
        throw new Error("Practice was replaced in another session");
      records.set(record.id, copy(practiceRecordSchema.parse(record)));
    },
    async remove(id, expectedGeneration) {
      if (expectedGeneration !== undefined && expectedGeneration !== generation)
        throw new Error("Practice was replaced in another session");
      records.delete(id);
    },
    async replace(next) {
      const parsed = z.array(practiceRecordSchema).max(20_000).parse(next);
      generation += 1;
      records.clear();
      for (const record of parsed) records.set(record.id, copy(record));
    },
  };
}

export function createIndexedDbPracticeStore(): PracticeStore {
  return {
    generation: learningGeneration,
    async list() {
      const rows = await learningTransaction(PRACTICE_STORE, "readonly", (store) => store.getAll());
      return rows.flatMap((row: unknown) => {
        const parsed = practiceRecordSchema.safeParse(row);
        return parsed.success ? [parsed.data] : [];
      });
    },
    async get(id) {
      const row = await learningTransaction(PRACTICE_STORE, "readonly", (store) => store.get(id));
      const parsed = practiceRecordSchema.safeParse(row);
      return parsed.success ? parsed.data : null;
    },
    async put(record, generation) {
      const parsed = practiceRecordSchema.parse(record);
      const db = await openLearningDatabase();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction([PRACTICE_STORE, META_STORE], "readwrite");
        const request = tx.objectStore(META_STORE).get("generation");
        request.onsuccess = () => {
          if (generation !== undefined && (request.result?.value || 0) !== generation) {
            tx.abort();
            return;
          }
          tx.objectStore(PRACTICE_STORE).put(parsed);
        };
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onabort = tx.onerror = () => {
          db.close();
          reject(tx.error || new Error("Practice was replaced in another tab"));
        };
      });
    },
    async remove(id, generation) {
      const db = await openLearningDatabase();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction([PRACTICE_STORE, META_STORE], "readwrite");
        const request = tx.objectStore(META_STORE).get("generation");
        request.onsuccess = () => {
          if (generation !== undefined && (request.result?.value || 0) !== generation) {
            tx.abort();
            return;
          }
          tx.objectStore(PRACTICE_STORE).delete(id);
        };
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onabort = tx.onerror = () => {
          db.close();
          reject(tx.error || new Error("Practice was replaced in another tab"));
        };
      });
    },
    async replace(records) {
      const parsed = z.array(practiceRecordSchema).max(20_000).parse(records);
      // Backups are small; the review store imports both stores atomically.
      await learningTransaction(PRACTICE_STORE, "readwrite", (store) => {
        store.clear();
        for (const record of parsed) store.put(record);
        return store.count();
      });
    },
  };
}

let defaultPractice: PracticeStore | null = null;
export function getPracticeStore(): PracticeStore {
  return (defaultPractice ||=
    typeof indexedDB === "undefined"
      ? createMemoryPracticeStore()
      : createIndexedDbPracticeStore());
}

export function exposureFromRecord(record: PracticeRecord): BranchExposure | null {
  const parsed = z
    .object({
      id: z.string(),
      openingId: z.string(),
      name: z.string(),
      firstSeen: z.number(),
      lastSeen: z.number(),
      encounters: z.number().nonnegative(),
      completed: z.number().nonnegative(),
      challenges: z.number().nonnegative(),
      preferred: z.boolean(),
    })
    .safeParse(record.value);
  return record.kind === "exposure" && parsed.success ? parsed.data : null;
}

/** Validate imported checkpoints before any task or route is restored. */
export function checkpointFromRecord(record: PracticeRecord | null): PracticeCheckpoint | null {
  if (!record || record.kind !== "checkpoint") return null;
  const parsed = z
    .object({
      session: z.object({
        version: z.literal(1),
        id: z.string(),
        slug: z.string(),
        mode: z.enum(["learn", "recall", "branches", "review"]),
        startedAt: z.number(),
        updatedAt: z.number(),
        scope: z.object({
          prefixSans: z.array(z.string().max(12)).max(80),
          decisions: z.number().int().min(1).max(15).nullable(),
          branchId: z.string().nullable(),
          challenge: z.boolean(),
          recent: z.boolean(),
        }),
        outcomes: z.record(
          z.object({
            task: z.object({
              id: z.string(),
              courseId: z.string(),
              openingId: z.string(),
              contentVersion: z.number(),
              fen: z.string(),
              positionKey: z.string(),
              student: z.enum(["w", "b"]),
              expected: z.string().max(12),
              accepted: z.array(z.string().max(12)).max(64),
              cue: z.string(),
            }),
            firstCorrect: z.boolean().nullable(),
            firstPlayed: z.string().nullable(),
            support: z.enum(["none", "goal", "piece", "candidates", "answer"]),
            answerRevealed: z.boolean(),
            attempts: z.number().nonnegative(),
            correct: z.boolean(),
            result: z.enum(["independent", "cued", "repaired", "missed", "studied"]),
          })
        ),
        repairTasks: z.array(z.string()).max(4).default([]),
        repairing: z.boolean().default(false),
        completedDecisions: z.number().int().min(0).max(80).default(0),
        attempts: z
          .array(
            z.object({
              id: z.string(),
              taskId: z.string(),
              at: z.number(),
              played: z.string().nullable(),
              correct: z.boolean(),
              support: z.enum(["none", "goal", "piece", "candidates", "answer"]),
            })
          )
          .max(500),
      }),
      route: z.array(z.string().max(12)).max(80),
      feedback: z
        .object({
          taskId: z.string(),
          title: z.string(),
          played: z.string().nullable(),
          expected: z.string(),
          result: z.enum(["independent", "cued", "repaired", "missed", "studied"]),
          why: z.string(),
          comparison: z.string().nullable(),
          nextLabel: z.string(),
          example: z.string().optional(),
        })
        .nullable()
        .default(null),
      review: z.unknown().optional(),
    })
    .safeParse(record.value);
  if (!parsed.success) return null;
  const data = parsed.data;
  const review = z
    .object({
      queue: z.array(z.string()).max(20),
      returns: z.array(z.string()).max(20),
      answers: z.array(z.unknown()).max(40),
      total: z.number().min(0).max(8),
      cardId: z.string().nullable(),
      pending: z
        .object({
          nextId: z.string().nullable(),
          againId: z.string().nullable(),
        })
        .nullable(),
    })
    .safeParse(data.review);
  return { ...data, review: review.success ? review.data : undefined };
}
